import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "gpv:pending-ref";

/**
 * Mounted once near the app root. Two responsibilities:
 *
 * 1. On first load, capture `?ref=CODE` from the URL and stash it in
 *    localStorage so we don't lose it through the sign-up flow.
 * 2. When a user becomes signed in, attempt to claim the pending code
 *    once (idempotent - the RPC rejects duplicates).
 */
const ReferralCaptureBoundary = () => {
  const { user } = useAuth();

  // Capture the ref param on initial mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("ref");
      if (code) {
        window.localStorage.setItem(STORAGE_KEY, code.toUpperCase());
      }
    } catch {
      // ignore
    }
  }, []);

  // When the user is signed in, claim once.
  useEffect(() => {
    if (!user) return;
    if (typeof window === "undefined") return;
    const code = window.localStorage.getItem(STORAGE_KEY);
    if (!code) return;
    let cancelled = false;
    (async () => {
      try {
        await (supabase as any).rpc("claim_owner_referral", { p_code: code });
      } catch {
        // best-effort - silently ignore
      } finally {
        if (!cancelled) window.localStorage.removeItem(STORAGE_KEY);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  return null;
};

export default ReferralCaptureBoundary;
